import React, { useContext, useEffect, useState } from "react";
import * as Yup from "yup";
import { useFormik } from "formik";
import { useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useTranslation } from "react-i18next";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import InputLabel from "@mui/material/InputLabel";
import TextField from "@mui/material/TextField";

import Layout from "../Layout/Layout";
import InformationModal from "../Modals/ConfirmationModal/InformationModal";

import { addIdentificationType } from "../../services/ParametersService";

import { ParametroContext } from "../../context/parametro/ParametroContext";

const CrearIdentificacion = () => {
  const { t } = useTranslation("parametros");
  const [openInformationModal, setOpenInformationModal] =
    useState<boolean>(false);
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const [clickCancelar, setClickCancelar] = useState<boolean>(false);
  const [clickGuardar, setClickGuardar] = useState<boolean>(false);
  const [modalTitle, setModalTitle] = useState<string>("");
  const [modalMessage, setModalMessage] = useState<string>("");
  const [disabledOnSubmit, setDisabledOnSubmit] = useState<boolean>(false);

  const navigate = useNavigate();

  const { getAccessTokenSilently } = useAuth0();

  const { setTabValue, tabValue } = useContext(ParametroContext);

  const formik = useFormik({
    initialValues: {
      label: "",
    },
    validationSchema: Yup.object({
      label: Yup.string()
        .max(55)
        .required(t("identificaciones.crear.validation.nameRequired")),
    }),
    onSubmit: async (data) => {
      setDisabledOnSubmit(true);
      const token = await getAccessTokenSilently();
      const result = await addIdentificationType(token, data);
      formik.resetForm();

      if (result.status === 201) {
        setTabValue(2);
        navigate(-1);
      }
    },
  });

  const handleOpenModal = () => {
    setOpenInformationModal(true);
  };

  const handleCloseModal = () => {
    setOpenInformationModal(false);
  };

  const handleClickGuardar = () => {
    setIsDirty(false);
    setClickCancelar(false);
    setClickGuardar(true);
    setModalTitle(t("identificaciones.crear.modals.createTitle"));
    setModalMessage(t("identificaciones.crear.modals.createMessage"));
    handleOpenModal();
  };

  const handleClickDirtyCancelar = () => {
    setIsDirty(true);
    setClickGuardar(false);
    setClickCancelar(true);
    setModalTitle(t("identificaciones.crear.modals.cancelChangesTitle"));
    setModalMessage(t("identificaciones.crear.modals.cancelChangesMessage"));
    handleOpenModal();
  };

  const handleDeshacerCancelar = () => {
    formik.resetForm();
    navigate(-1);
  };

  const handleSaveButton = () => {
    formik.submitForm();
  };

  useEffect(() => {
    document.title = `${window._env_.REACT_APP_EMPRESA_NAME} - ${t("identificaciones.crear.pageTitle")}`;

    if (tabValue !== 2) {
      setTabValue(2);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabValue]);

  return (
    <Layout title={t("identificaciones.crear.layoutTitle")} noIcon>
      <Grid container marginTop="12px" borderRadius="6px">
        <Grid bgcolor="white" item xs={4} borderRadius="6px">
          <Box
            sx={{
              borderRadius: "6px",
              display: "flex",
              backgroundColor: "white",
              height: "100%",
              width: "100%",
              paddingRight: "18px",
            }}
          >
            <Box sx={{ flexGrow: 1 }}>
              <Box
                component="form"
                autoComplete="off"
                borderRadius="6px"
                sx={{ backgroundColor: "white", height: "100%" }}
                onSubmit={formik.handleSubmit}
              >
                <Grid container justifyContent="space-between">
                  <Grid item xs={8} sx={{ py: "18px", pl: "18px" }}>
                    <Box
                      display="flex"
                      justifyContent="flex-start"
                      marginBottom="4px"
                    >
                      <InputLabel sx={{ color: "black" }} htmlFor="label-input">
                        {t("identificaciones.crear.fields.name")}
                      </InputLabel>
                    </Box>
                    <TextField
                      id="label-input"
                      error={Boolean(
                        formik.touched.label && formik.errors.label
                      )}
                      fullWidth
                      helperText={formik.touched.label && formik.errors.label}
                      name="label"
                      onBlur={formik.handleBlur}
                      onChange={formik.handleChange}
                      type="text"
                      value={formik.values.label}
                      variant="filled"
                      placeholder={t("identificaciones.crear.fields.namePlaceholder")}
                      InputProps={{
                        disableUnderline: true,
                        sx: {
                          borderRadius: "6px",
                          ".MuiInputBase-input": {
                            paddingBottom: "16px",
                            paddingTop: "16px",
                          },
                        },
                      }}
                    />
                  </Grid>
                </Grid>
                <Grid container maxWidth="sm" paddingBottom="32px">
                  <Box sx={{ display: "flex" }}>
                    <Button
                      sx={{ marginLeft: "18px" }}
                      variant="contained"
                      color="primary"
                      onClick={handleClickGuardar}
                      disabled={!formik.dirty || !formik.values.label}
                    >
                      {t("actions.save", { ns: "common" })}
                    </Button>

                    {formik.dirty ? (
                      <Button
                        sx={{ marginLeft: "18px" }}
                        variant="contained"
                        color="error"
                        onClick={handleClickDirtyCancelar}
                      >
                        {t("actions.cancel", { ns: "common" })}
                      </Button>
                    ) : (
                      <Button
                        sx={{ marginLeft: "18px" }}
                        variant="contained"
                        color="error"
                        onClick={() => navigate(-1)}
                      >
                        {t("actions.cancel", { ns: "common" })}
                      </Button>
                    )}
                  </Box>
                </Grid>
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>

      {openInformationModal && (
        <InformationModal
          title={modalTitle}
          message={modalMessage}
          openModal={openInformationModal}
          onClose={handleCloseModal}
          saveForm={handleSaveButton}
          isDirty={isDirty}
          clickCancelar={clickCancelar}
          handleDeshacerCancelar={handleDeshacerCancelar}
          guardar={clickGuardar}
          guardarMessage={modalMessage}
          clickEliminar={false}
          disabledOnSubmit={disabledOnSubmit}
        />
      )}
    </Layout>
  );
};

export default CrearIdentificacion;
